"use client";
import NoSsr from "../common/noSsr";
import Section from "../common/section";
import Image, { StaticImageData } from "next/image";
import { type IconType } from "../icon";
import { forwardRef, LegacyRef } from "react";
import { StaticImport } from "next/dist/shared/lib/get-img-props";

export type BubbleGalleryImage = {
  src: string;
  bgClass?: string;
  // overrides for gallery props
  ring?: boolean;
  imageContain?: boolean;
  sizeFactor?: number;
  durationFactor?: number;
};

export type BubbleGalleryProps = {
  id?: string;
  title?: string | JSX.Element;
  body?: string;
  afterAction?: JSX.Element | boolean;

  images: BubbleGalleryImage[];
  bgClass?: string;

  noRings?: boolean;
  noBubbles?: boolean;
  imageContain?: boolean;

  animation?: "rise" | "fall" | "none"; // defaults to rise
  sameSize?: boolean;
  sizeFactor?: number;
  durationFactor?: number;
  smallSizeFactor?: number; // 0.5 if not set
  colourVariant?: string;

  right?: boolean;

  // heart icon image
  iconImage?: string | StaticImageData | StaticImport;
  iconImgLeft?: boolean;
  iconImgPosition?: string;
  hideIconImageOnMobile?: boolean;

  textSectionBackground?: string; // background layer for flying logo
};

const imageRingClass = `
  absolute inset-0 rounded-full
  ring-1 ring-inset ring-pupleblue/30 hover:ring-purpleblue/60
`;
const imageClass = (imageContain?: boolean) => `
  ${imageContain ? "object-contain" : "object-cover"}
  h-full w-full
`;

// const IMAGE_BASE_SIZE = 128
// const imageSize = IMAGE_BASE_SIZE * 4
// const BUBBLE_BASE_SIZE = 48

// OLD DETERMINISTIC ONES -- before we realised we should be making this client-only
// from 96 to 192px, 8 to 16s, -8 to 8s intervals
// Math.round(96 * Math.random() + 96), `${Math.round(8 * Math.random()) + 8}s`, `${Math.round(16 * Math.random()) - 8}s`
// const RANDOM_HEIGHTS = [170, 116, 122, 185, 177, 118, 180, 144]
// const RANDOM_DURATIONS = [14, 9, 11, 16, 10, 8, 15, 10]
// const RANDOM_DELAYS = [-6, 7, 6, -4, 6, -5, 7, -1]
// from 32 to 64px, 8 to 16s + 0.5s, -7.5 to 8.5s in intervals
// making all durations off by a bit so they're never awkwardly in sync with logos
// const RANDOM_BUBBLE_HEIGHTS = [45, 58, 41, 63, 61, 58, 42, 54, 40, 36]
// const RANDOM_BUBBLE_DURATIONS = [10.5, 12.5, 15.5, 16.5, 10.5, 15.5, 9.5, 10.5, 12.5, 11.5]
// const RANDOM_BUBBLE_DELAYS = [-3.5, 8.5, -5.5, 4.5, -1.5, 4.5, -1.5, -6.5, 8.5, 5.5]

// NEW FLEXIBLE & RANDOMISED
// let's say we have at least 8 images for this to work.
// for 'randomness': we want some low, some medium and some large values
// what we do is: for len(images) on a side, we split the range into ranges
// randomly assign each image to a range per attribute, then randomise within that ranges
// we need to randomise: image height, image LR, animation duration, animation delay
// bubbles are fine, we just do left-10-90%

// our constants required are: minimums, random-ranges. bubbles have same duration but different heights
const HEIGHT_MIN = 90;
const HEIGHT_RANDOM = 60;
const LR_MIN = 10;
const LR_RANDOM = 80;
const DURATION_MIN = 6;
const DURATION_RANDOM = 10;
const DELAY_MIN = -8;
const DELAY_RANDOM = 8;
const BUBBLE_HEIGHT_MIN = 48;
const BUBBLE_HEIGHT_RANDOM = 96;
const imageImportSize = (HEIGHT_MIN + HEIGHT_RANDOM) * 2;

// per side
const BUBBLE_COLOURS = [
  "bg-green/60",
  "bg-orange/60",
  "bg-[#cc9629]/60", // ct default gold
  "bg-[#3399cc]/60", // ct default blue
  "bg-[#9545e6]/60", // ct default purple
];
const N_BUBBLES = 5;
const STATIC_TOPS = [
  "top-[10%]",
  "top-[80%]",
  "top-[40%]",
  "top-[45%]",
  "top-[85%]",
];

// generate random permutations of 0 => len
const generateShuffleRange = (len: number) =>
  new Array(len)
    .fill(null)
    .map((_, i) => ({ i, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map((x) => x.i);
// generate a range order for each randomised property
const shuffleRanges = (len: number) => ({
  height: generateShuffleRange(len),
  lr: generateShuffleRange(len),
  duration: generateShuffleRange(len),
  delay: generateShuffleRange(len),
});
const generateFromRange = (
  i: number,
  range: number[],
  min: number,
  randomSize: number,
) => min + (Math.random() * range[i]! * randomSize) / range.length;
const makeImageStyle = (
  ranges: ReturnType<typeof shuffleRanges>,
  i: number,
  sameSize?: boolean,
  sizeFactor?: number,
  durationFactor?: number,
) => ({
  height:
    (sameSize
      ? HEIGHT_MIN + HEIGHT_RANDOM / 2
      : generateFromRange(i, ranges.height, HEIGHT_MIN, HEIGHT_RANDOM)) *
    (sizeFactor ?? 1),
  left: `${generateFromRange(i, ranges.lr, LR_MIN, LR_RANDOM)}%`,
  animationDuration: `${generateFromRange(i, ranges.duration, DURATION_MIN, DURATION_RANDOM) * (durationFactor ?? 1)}s`,
  animationDelay: `${generateFromRange(i, ranges.delay, DELAY_MIN, DELAY_RANDOM) * (durationFactor ?? 1)}s`,
});
const makeBubbleStyle = (
  ranges: ReturnType<typeof shuffleRanges>,
  i: number,
  sameSize?: boolean,
  durationFactor?: number,
) => ({
  height: sameSize
    ? BUBBLE_HEIGHT_MIN + BUBBLE_HEIGHT_RANDOM / 2
    : generateFromRange(
        i,
        ranges.height,
        BUBBLE_HEIGHT_MIN,
        BUBBLE_HEIGHT_RANDOM,
      ),
  left: `${generateFromRange(i, ranges.lr, LR_MIN, LR_RANDOM)}%`,
  animationDuration: `${generateFromRange(i, ranges.duration, DURATION_MIN, DURATION_RANDOM) * (durationFactor ?? 1)}s`,
  animationDelay: `${generateFromRange(i, ranges.delay, DELAY_MIN, DELAY_RANDOM) * (durationFactor ?? 1)}s`,
});

const BubbleGallerySection = (props: BubbleGalleryProps, ref: LegacyRef<HTMLElement> | undefined) => {
  if (!props.images || props.images.length < 6) {
    // logger.error(`Bubble gallery section requires min 6 images, received ${props.images.length}`)
    console.log(
      `Bubble gallery section requires min 6 images, ${!props.images ? "received 0 image" : `received ${props.images.length}`}`,
    );
    return <></>;
  }

  const smallSizeFactor = props.smallSizeFactor ?? 0.5;
  const animationClass =
    props.animation === "none" ? undefined : (props.animation ?? "rise");
  const titleClass = `mt-2 font-bold tracking-tight text-desk-resp-4_5xl xl:text-[2rem]`;
  const bubbleClass = (top: string) =>
    `absolute aspect-square rounded-full ${animationClass ?? top}`;
  const imageFrameClass = (top: string) =>
    `absolute aspect-square rounded-full ${animationClass ?? top} z-10 ${props.noRings ? "" : "overflow-hidden"}`;

  // process and assign images
  const lenL = Math.ceil(props.images.length / 2); // 8 => 4, slices 0-4 (len 4) & 4+ (len 4); 9 => 5, slices 0-5 (len 5) & 5+ (len 4)
  const lenR = props.images.length - lenL;
  const lRanges = shuffleRanges(lenL);
  const rRanges = shuffleRanges(lenR);
  const imagesL = props.images.slice(0, lenL).map((img, i) => ({
    ...img,
    ring: img.ring ?? !props.noRings,
    imageContain: img.imageContain ?? props.imageContain,
    style: makeImageStyle(
      lRanges,
      i,
      props.sameSize,
      img.sizeFactor ?? props.sizeFactor,
      img.durationFactor ?? props.durationFactor,
    ),
  }));
  const imagesR = props.images.slice(lenL).map((img, i) => ({
    ...img,
    ring: img.ring ?? !props.noRings,
    imageContain: img.imageContain ?? props.imageContain,
    style: makeImageStyle(
      rRanges,
      i,
      props.sameSize,
      img.sizeFactor ?? props.sizeFactor,
      img.durationFactor ?? props.durationFactor,
    ),
  }));
  // process and assign bubbles
  const lBubbleRanges = shuffleRanges(N_BUBBLES);
  const rBubbleRanges = shuffleRanges(N_BUBBLES);
  const bubblesL = new Array(N_BUBBLES)
    .fill(null)
    .map((_, i) =>
      makeBubbleStyle(lBubbleRanges, i, props.sameSize, props.durationFactor),
    );
  const bubblesR = new Array(N_BUBBLES)
    .fill(null)
    .map((_, i) =>
      makeBubbleStyle(rBubbleRanges, i, props.sameSize, props.durationFactor),
    );

  return (
    <NoSsr>
      <Section
        id={props.id}
        bgClass={props.bgClass}
        className={`grid w-full md:min-h-[min(60vh,600px)] 2xl:min-h-[min(60vh,700px)]`}
        flexHeight
        ref={ref}
      >
        <div className="relative mx-auto grid h-full w-full flex-1 grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
          {/* max-md: top picture bubbles */}
          <div className="relative h-[20rem] md:hidden">
            {/* Gallery image bubbles */}
            {imagesL.map((img, i) => (
              <div
                key={i}
                data-cy={`bubble-S${i}`}
                style={{
                  ...img.style,
                  height: img.style.height * smallSizeFactor,
                }}
                className={`${imageFrameClass(STATIC_TOPS[i % STATIC_TOPS.length] as string)} ${img.bgClass ?? ""} translate-x-[-${img.style.height / 2}px]`}
              >
                <Image
                  src={img.src}
                  alt=""
                  height={imageImportSize * (props.sizeFactor ?? 1)}
                  width={imageImportSize * (props.sizeFactor ?? 1)}
                  className={imageClass(img.imageContain)}
                />
                {img.ring && <div className={imageRingClass} />}
              </div>
            ))}
            {/* Random bubbles */}
            {!props.noBubbles && (
              <>
                {bubblesL.map((style, i) => (
                  <div
                    key={i}
                    style={style}
                    className={`${bubbleClass(STATIC_TOPS[i % STATIC_TOPS.length] as string)} ${BUBBLE_COLOURS[i % BUBBLE_COLOURS.length]} translate-x-[-${style.height / 2}px]`}
                  />
                ))}
              </>
            )}
          </div>

          {/* xl: left gallery (4 bubbles) */}
          <div className="relative hidden h-full xl:block">
            {/* Gallery image bubbles */}
            {imagesL.map((img, i) => (
              <div
                key={i}
                data-cy={`bubble-L${i}`}
                style={img.style}
                className={`${imageFrameClass(STATIC_TOPS[i % STATIC_TOPS.length] as string)} ${img.bgClass ?? ""} translate-x-[-${img.style.height / 2}px]`}
              >
                <Image
                  src={img.src}
                  alt=""
                  height={imageImportSize * (props.sizeFactor ?? 1)}
                  width={imageImportSize * (props.sizeFactor ?? 1)}
                  className={imageClass(img.imageContain)}
                />
                {img.ring && <div className={imageRingClass} />}
              </div>
            ))}
            {/* Random bubbles */}
            {!props.noBubbles && (
              <>
                {bubblesL.map((style, i) => (
                  <div
                    key={i}
                    style={style}
                    className={`${bubbleClass(STATIC_TOPS[i % STATIC_TOPS.length] as string)} ${BUBBLE_COLOURS[i % BUBBLE_COLOURS.length]} translate-x-[-${style.height / 2}px]`}
                  />
                ))}
              </>
            )}
          </div>

          {/* text section */}
          <div
            className={`relative z-30 mx-auto flex max-w-3xl ${props.textSectionBackground} md:bg-none md:py-32 xl:col-span-2 xl:py-48 xl:text-center ${props.iconImage ? "mb-24 md:mb-0" : "py-8"}`}
          >
            <div className={`my-auto px-6 md:mr-auto md:pl-12 md:pr-0 xl:px-0`}>
              {typeof props.title === "string" ? (
                <h2 className={titleClass}>{props.title}</h2>
              ) : (
                <div className={titleClass}>{props.title}</div>
              )}
              {props.body && (
                <div className={`text-desk-resp-xl mt-6 text-white`}>
                  {props.body}
                </div>
              )}
              {props.afterAction && (
                <div className="mt-8 w-fit xl:mx-auto">{props.afterAction}</div>
              )}
            </div>
          </div>

          {/* md: right gallery (4 bubbles) */}
          <div className="relative hidden h-full md:block">
            {/* Gallery images */}
            {imagesR.map((img, i) => (
              <div
                key={i}
                data-cy={`bubble-R${i}`}
                style={img.style}
                className={`${imageFrameClass(STATIC_TOPS[(i + lenL) % STATIC_TOPS.length] as string)} ${img.bgClass ?? ""} translate-x-[-${img.style.height / 2}px]`}
              >
                <Image
                  src={img.src}
                  alt=""
                  height={imageImportSize * (props.sizeFactor ?? 1)}
                  width={imageImportSize * (props.sizeFactor ?? 1)}
                  className={imageClass(img.imageContain)}
                />
                {img.ring && <div className={imageRingClass} />}
              </div>
            ))}
            {!props.noBubbles && (
              <>
                {bubblesR.map((style, i) => (
                  <div
                    key={i}
                    style={style}
                    className={`${bubbleClass(STATIC_TOPS[i % STATIC_TOPS.length] as string)} ${BUBBLE_COLOURS[i % BUBBLE_COLOURS.length]} translate-x-[-${style.height / 2}px]`}
                  />
                ))}
              </>
            )}
          </div>

          {props.iconImage && (
            <Image
              src={props.iconImage}
              alt="heart-image"
              className={`absolute bottom-0 z-20 w-72 md:w-96 lg:w-[28rem] xl:w-[32rem] ${props.iconImgLeft ? (props.iconImgPosition ?? `-left-12 right-0 lg:-left-14 xl:-left-20`) : (props.iconImgPosition ?? `-right-14 md:-right-14 xl:-right-20`)} ${props.hideIconImageOnMobile ? "hidden xs:block" : ""}`}
            />
          )}
        </div>
      </Section>
    </NoSsr>
  );
};

export default forwardRef(BubbleGallerySection);
